import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { AppConfig } from "../../api/config/config";
import DrugIcon from "../../assets/images/drug_test.png";
import Ic_save from "../../assets/images/icons/add-square-03.svg";
import Ic_edit from "../../assets/images/icons/edit-icon.svg";
import Ic_link from "../../assets/images/icons/link-angled.svg";
import menu from "../../assets/images/icons/menu.svg";
import Ic_pdf from "../../assets/images/icons/pdf.svg";
import Ic_print from "../../assets/images/icons/print.svg";
import Ic_delete from "../../assets/images/icons/trash.svg";
import {
  AccordionItem,
  ConfirmationPopup,
  CustomAccordion,
  Dropdown,
  GeneralButton,
  Loader,
  Modal,
} from "../../components";
import { EndPoints } from "../../Constants/endpoints";
import { useGetData, useGetPersonalInfo, usePostData } from "../../hooks";
import i18n from "../../i18n";
import {
  EditPrescriptionDrug,
  EditPrescriptionType,
  GeneralResponse,
} from "../../interface/GeneralResponse";
import { updatePrescription } from "../../redux/reducers/prescriptionsSlice";
import { setSelectedPrescription } from "../../redux/reducers/selectedPrescriptionSlice";
import { useToast } from "../../Services/ToastContext";
import { handleDownloadPdf, handlePrint } from "../../utils/fileUtils";
import AddExistPrescriptionToTemplates from "./AddExistPrescriptionToTemplates";
import { SendViaWhatsappBtn } from "./components";

interface PrescriptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  prescriptionId: number;
  onEdit: (id: number) => void;
  onDeleted: () => void;
  OnLinkPatient?: (Id: number) => void;
}

const PrescriptionDetailsModal: React.FC<PrescriptionDetailsModalProps> = ({
  isOpen,
  onClose,
  prescriptionId,
  onEdit,
  onDeleted,
  OnLinkPatient,
}) => {
  const { showToast } = useToast();
  const { id: patientId } = useParams();
  const { isAllowed } = useGetPersonalInfo();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaveToTemplateOpen, setIsSaveToTemplateOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPrint, setIsPrint] = useState<boolean>(false);

  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const drugElement = (
    drug: { label: string; value: string | number },
    index: number,
  ) => (
    <div key={index} className="me-6 flex flex-col gap-2">
      <span className="gap-1 text-body-text">{drug.label}</span>
      <span className="text-wrap text-primary-text">{drug.value}</span>
    </div>
  );

  const drugDetails = (drug: EditPrescriptionDrug) => [
    {
      label: t("dose"),
      value: drug.DosageNum
        ? `${drug.DosageNum} ${drug?.DosageFormUnitName ?? drug?.DosageForm}`
        : t("not_specified"),
    },
    {
      label: t("times"),
      value: drug.Times || t("not_specified"),
    },
    {
      label: t("duration"),
      value: drug.Duration || t("not_specified"),
    },
    {
      label: t("medication_period"),
      value: drug.Meal || t("not_specified"),
    },
    {
      label: t("before_sleep"),
      value: drug.Sleep ? t("yes") : t("no"),
    },
    {
      label: t("frequency_type"),
      value: drug.Frequency || t("not_specified"),
    },
  ];

  const {
    data: prescriptionData,
    isLoading,
    isError,
    refetch,
  } = useGetData<{ Prescription: EditPrescriptionType }>(
    EndPoints.GetPrescriptions,
    {
      enabled: isOpen,
    },
    prescriptionId,
  );

  const prescription = prescriptionData?.Data?.Prescription;

  const { mutate, isPending } = usePostData(
    `${EndPoints.PrintPrescription}/${prescription?.Id}`,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (prescription) {
      dispatch(setSelectedPrescription(prescription as any));
      dispatch(updatePrescription(prescription as any));
    }
  }, [prescription, dispatch]);

  useEffect(() => {
    if (isOpen && prescriptionId) {
      refetch();
    }
  }, [isOpen, prescriptionId, refetch]);

  const handleExportPDF = (pdfData: GeneralResponse<any>) => {
    try {
      const byteCharacters = atob(pdfData?.Data?.FileContents);
      const byteNumbers = Array.from(byteCharacters, (char) =>
        char.charCodeAt(0),
      );
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const objectUrl = URL.createObjectURL(blob);
      if (isPrint) {
        const iframe = document.createElement("iframe");
        iframe.style.position = "absolute";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "none";
        iframe.src = objectUrl;
        document.body.appendChild(iframe);

        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        };
      } else {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = pdfData?.Data?.FileDownloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error: any) {
      showToast(
        // GetPdfError?.message ??
        error?.message ?? t("an_error_occurred_please_try_again"),
        "error",
      );
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
    setIsDropdownOpen(false);
  };

  const { id } = useParams();

  const { mutate: mutateDelete } = usePostData(
    `${EndPoints.DeletePrescription}/${prescriptionId}`,
  );

  const handleConfirmDelete = async () => {
    try {
      mutateDelete(
        {},
        {
          onSuccess: () => {
            showToast(t("deleted_successfully"), "success");
            onDeleted();
            queryClient.refetchQueries({
              queryKey: [`${EndPoints.GetPrescriptionToPatient}/${id}`],
              exact: false,
            });
            queryClient.refetchQueries({
              queryKey: [EndPoints.GetPrescriptions],
              exact: false,
            });
            onClose();
          },
        },
      );
    } catch (error) {
      showToast(t("failed_to_delete"), "error");
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const handleEditClick = () => {
    setIsDropdownOpen(false);
    onEdit(prescriptionId);
  };

  const renderContent = () => {
    if (isLoading) {
      return <Loader />;
    }

    if (isError || !prescription) {
      return (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <p className="text-error">{t("prescription_not_found")}</p>
        </div>
      );
    }

    const formattedTimestamp = String(prescription.CreatedAt).split("٫")?.[0];

    const menuItems = [
      {
        label: t("edit"),
        onClick: handleEditClick,
        icon: Ic_edit,
      },
      {
        label: t("link_to_patient"),
        onClick: () => {
          OnLinkPatient && OnLinkPatient(prescriptionId);
        },
        icon: Ic_link,
        hide: !OnLinkPatient,
      },
      {
        label: t("export_as_pdf"),
        className: `${isDownloading ? "!cursor-not-allowed" : ""}`,
        onClick: () => {
          setIsDownloading(true);

          !isDownloading &&
            mutate(
              {},
              {
                onSuccess: (data) => {
                  handleDownloadPdf(
                    data?.Data?.FileContents,
                    data?.Data?.FileDownloadName,
                    "PDF",
                    setIsDownloading,
                  );
                },
              },
            );
        },
        icon: Ic_pdf,
      },
      {
        label: t("save_as_template"),
        onClick: () => setIsSaveToTemplateOpen(true),
        icon: Ic_save,
        hide: !patientId,
      },
      {
        label: t("delete"),
        onClick: handleDelete,
        icon: Ic_delete,
      },
    ];

    return (
      <div className="flex flex-1 flex-col justify-between gap-2">
        {/* <div className="flex items-center gap-2 text-body-text">
            <span className="whitespace-nowrap">
              {t("created_on")}{" "}
              {moment
                .unix(Number(formattedTimestamp))
                .locale(i18n?.language?.slice(0, 2))
                .format("DD MMMM YYYY")}
            </span>
            <div className="h-[2px] w-full bg-body-text" />
          </div>  */}

        <div className="h-[38dvh] grow overflow-auto border-primary-bg">
          <CustomAccordion expandAll={false}>
            <div className="flex flex-col gap-2">
              {prescription.Drugs.map((drug, i) => (
                <div className="rounded-lg bg-primary-bg px-3">
                  <AccordionItem
                    key={drug.PackageId || i}
                    iconClass="!rounded-full border"
                    totalClass="!border-none"
                    titleIcon={AppConfig.SERVER_URL + drug.Image || DrugIcon}
                    title={
                      <div className="flex flex-col justify-center">
                        <h3 className={`text-start font-bold`}>
                          {`${drug.DrugName || drug.Name} ${drug.PackageName}`}
                        </h3>
                        {/* <div className="flex items-center gap-2 text-body-text">
                      <p className="text-sm font-semibold">{drug?.Facility}</p>
                      <span>.</span>
                      <p className="text-sm">{drug.DosageForm}</p>
                    </div> */}
                      </div>
                    }
                    expandAll={false}
                  >
                    <div className="mb-4 flex flex-wrap gap-4">
                      {drugDetails(drug).map((item, index) =>
                        drugElement(item, index),
                      )}
                    </div>
                    {drugElement(
                      {
                        label: t("instructions"),
                        value: drug.Instructions || t("not_specified"),
                      },
                      66,
                    )}
                  </AccordionItem>
                </div>
              ))}
            </div>
          </CustomAccordion>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          {isAllowed && (
            <Dropdown
              menuItems={menuItems}
              children={
                <div
                  className="flex cursor-pointer items-center gap-2 rounded-lg bg-InputFieldColor px-3 py-2"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <img src={menu} alt="menu" className="w-6" />
                  <span className="">{t("more")}</span>
                </div>
              }
              isOpen={isDropdownOpen}
              setIsOpen={setIsDropdownOpen}
            />
          )}
          <GeneralButton
            title={t("print")}
            color="lite"
            icon={Ic_print}
            isLoading={isPending && isPrinting}
            onClick={() => {
              setIsPrinting(true);
              mutate(
                {},
                {
                  onSuccess: (data) => {
                    handlePrint(
                      data?.Data?.FileContents,
                      data?.Data?.FileDownloadName,
                      setIsPrinting,
                    );
                  },
                },
              );
            }}
          />
          <SendViaWhatsappBtn id={prescriptionId} />
        </div>
      </div>
    );
  };

  return (
    <>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          header={
            prescriptionData?.Data?.Prescription.Case ||
            t("prescription_details")
          }
          ariaLabel="Prescription Details Modal"
          customStyle="min-h-[70dvh] !max-w-2xl"
        >
          {renderContent()}
        </Modal>
      )}

      {isDeleteModalOpen && (
        <ConfirmationPopup
          title={t("confirm_deletion")}
          description={t("are_you_sure_you_want_to_delete_this_prescription")}
          isModalOpen={isDeleteModalOpen}
          okayBtColor="red"
          okayBtText={t("delete")}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          ariaLabel={t("confirm_deletion")}
        />
      )}
      {isSaveToTemplateOpen && (
        <AddExistPrescriptionToTemplates
          isOpen={isSaveToTemplateOpen}
          templateData={prescription}
          onClose={() => setIsSaveToTemplateOpen(false)}
        />
      )}
    </>
  );
};

export default PrescriptionDetailsModal;
